import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CONFIG = {
  visitor_arrival:   { icon:'👥', label:'Visitor Arrival',   color:'bg-blue-50 border-blue-200 text-blue-800' },
  meeting_reminder:  { icon:'📅', label:'Meeting Reminder',   color:'bg-purple-50 border-purple-200 text-purple-800' },
  room_checkout:     { icon:'🏨', label:'Room Check-out',     color:'bg-orange-50 border-orange-200 text-orange-800' },
  pending_approval:  { icon:'⏳', label:'Pending Approval',   color:'bg-yellow-50 border-yellow-200 text-yellow-800' },
  general:           { icon:'ℹ️', label:'General',             color:'bg-gray-50 border-gray-200 text-gray-800' },
};

export default function Alerts() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const res = await api.get('/api/notifications'); setNotifications(res.data); }
    catch (err) { console.error('notifications:', err); setNotifications([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const markRead = async (id) => {
    try { await api.put(`/api/notifications/${id}/read`); load(); }
    catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try { await api.put('/api/notifications/read-all'); load(); }
    catch { /* ignore */ }
  };

  const unread = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Alerts & Notifications</h1>
          <p className="text-gray-500 text-sm">{unread} unread notification{unread !== 1 ? 's' : ''}</p>
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            ✅ Mark All Read
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">Loading...</div>
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">
          <div className="text-5xl mb-3">🔔</div>
          <p className="font-medium">No notifications</p>
          <p className="text-sm mt-1">You're all caught up!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.general;
            return (
              <div key={n.id}
                className={`border rounded-xl p-4 flex items-start gap-4 transition-opacity ${cfg.color} ${n.is_read ? 'opacity-60' : ''}`}>
                <span className="text-2xl mt-0.5">{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wide">{cfg.label}</span>
                    {!n.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span>}
                  </div>
                  <p className="text-sm font-medium">{n.message}</p>
                  <p className="text-xs mt-1 opacity-70">{new Date(n.created_at).toLocaleString('en-PK')}</p>
                </div>
                {!n.is_read && (
                  <button onClick={() => markRead(n.id)} className="text-xs underline opacity-70 hover:opacity-100 whitespace-nowrap">
                    Mark Read
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
